import api from './client';
import type { ApiResponse } from '@gokrew/models/generic';
import type {
  NeedsAttentionFeed,
  NeedsAttentionHideOptions,
  NeedsAttentionHideResult,
  NeedsAttentionPingResult,
  NeedsAttentionPrepareResult,
  NeedsAttentionDigestStatus,
  SignalSettingKey,
  SignalSettingsView,
  UpdateSignalSettingPayload,
} from '@gokrew/models/company-dashboard';
import type {
  CompanyLeaveCalendarResponse,
  CompanyLeaveRequestItem,
  CompanyLeaveRequestsResponse,
  LeaveDecisionContextResponse,
  GetAllocationJobsParams,
  GetCompanyLeaveCalendarParams,
  GetCompanyLeaveRequestsParams,
  GetCurrentFiscalStartYearResponse,
  GetLeaveRequestsSummaryParams,
  GetLeaveRequestsSummaryResponse,
  GetScheduledJobEmployeeDataParams,
  LeaveAttachmentContentType,
  LeaveAttachmentUploadTicket,
  LeaveJobResponse,
  ScheduledJobEmployeeResponse,
} from '@gokrew/models/leave-management';
import type {
  GetEmployeeLeaveWalletsResponse,
  GetCompanyLeaveWalletsParams,
} from '@gokrew/models/leave-wallets';
import type {
  AdminCreateLeaveRequestManagementPayload,
  ApproveLeaveParams,
  DeleteLeaveParams,
  LeaveDayCount,
  LeaveDayCountParams,
  RejectCompanyLeaveParams,
  WorkDayMinutesResponse,
} from '@gokrew/models/leaves';

export async function getLeaveRequestsSummary(
  params: GetLeaveRequestsSummaryParams
) {
  const { data } = await api.get<GetLeaveRequestsSummaryResponse>(
    `/companies/${params.companyId}/leave-requests-management/summary`,
    {
      params,
    }
  );

  return data;
}

export async function getCompanyLeaveRequests(
  params: GetCompanyLeaveRequestsParams
) {
  const { data } = await api.get<CompanyLeaveRequestsResponse>(
    `/companies/${params.companyId}/leave-requests-management`,
    { params }
  );

  return data;
}

export async function getCompanyLeaveRequest(params: {
  companyId: string;
  id: string;
  branchId?: string;
}) {
  const { companyId, id, branchId } = params;
  const { data } = await api.get<ApiResponse<CompanyLeaveRequestItem>>(
    `/companies/${companyId}/leave-requests-management/${id}`,
    { params: branchId ? { branchId } : undefined }
  );
  return data;
}

export async function getLeaveDecisionContext(params: {
  companyId: string;
  id: string;
  branchId?: string;
}) {
  const { companyId, id, branchId } = params;
  const { data } = await api.get<LeaveDecisionContextResponse>(
    `/companies/${companyId}/leave-requests-management/${id}/decision-context`,
    { params: branchId ? { branchId } : undefined }
  );
  return data;
}

export async function getNeedsAttention(params: {
  companyId: string;
  branchId?: string;
  /** Rows in the compact `items` (server default 6, at most 20). */
  rows?: number;
}) {
  const { companyId, branchId, rows } = params;
  const { data } = await api.get<ApiResponse<NeedsAttentionFeed>>(
    `/companies/${companyId}/needs-attention`,
    { params: { branchId, rows } }
  );
  return data;
}

/** "Notify" on a feed line; the server decides who is told. */
export async function pingNeedsAttention(params: {
  companyId: string;
  branchId?: string;
  itemId: string;
}) {
  const { companyId, branchId, itemId } = params;
  const { data } = await api.post<ApiResponse<NeedsAttentionPingResult>>(
    `/companies/${companyId}/needs-attention/ping`,
    { itemId },
    { params: branchId ? { branchId } : undefined }
  );
  return data;
}

/**
 * Hide a feed line (or every line of a rollup row) for the whole HR team.
 * The server answers the line ids it hid, which Undo sends back. A signal
 * line may be hidden for 30 or 90 days and carry a reason.
 */
export async function hideNeedsAttention(
  params: {
    companyId: string;
    branchId?: string;
    itemId: string;
  } & NeedsAttentionHideOptions
) {
  const { companyId, branchId, itemId, until, reason } = params;
  const { data } = await api.post<ApiResponse<NeedsAttentionHideResult>>(
    `/companies/${companyId}/needs-attention/hide`,
    { itemId, ...(until ? { until } : {}), ...(reason ? { reason } : {}) },
    { params: branchId ? { branchId } : undefined }
  );
  return data;
}

/**
 * "Prepare the meeting" on a feed line: talking points and an invitation.
 * Only the line id is sent; the server reads the line from the caller's own
 * feed and never sends a name to the model.
 */
export async function prepareNeedsAttention(params: {
  companyId: string;
  branchId?: string;
  itemId: string;
}) {
  const { companyId, branchId, itemId } = params;
  const { data } = await api.post<ApiResponse<NeedsAttentionPrepareResult>>(
    `/companies/${companyId}/needs-attention/prepare`,
    { itemId },
    { params: branchId ? { branchId } : undefined }
  );
  return data.data;
}

/** Whether the signed-in user receives the weekly summary (default off). */
export async function getNeedsAttentionDigest(companyId: string) {
  const { data } = await api.get<ApiResponse<NeedsAttentionDigestStatus>>(
    `/companies/${companyId}/needs-attention/digest`
  );
  return data.data;
}

/** Turns the weekly summary on or off for the signed-in user only. */
export async function updateNeedsAttentionDigest(
  companyId: string,
  enabled: boolean
) {
  const { data } = await api.put<ApiResponse<NeedsAttentionDigestStatus>>(
    `/companies/${companyId}/needs-attention/digest`,
    { enabled }
  );
  return data.data;
}

/** Smart signals settings (Company info): switches and thresholds. */
export async function getSignalSettings(companyId: string) {
  const { data } = await api.get<ApiResponse<SignalSettingsView>>(
    `/companies/${companyId}/needs-attention/signal-settings`
  );
  return data.data;
}

/** Saves one signal's switch and thresholds; answers every setting. */
export async function updateSignalSetting(
  companyId: string,
  type: SignalSettingKey,
  payload: UpdateSignalSettingPayload
) {
  const { data } = await api.put<ApiResponse<SignalSettingsView>>(
    `/companies/${companyId}/needs-attention/signal-settings/${type}`,
    payload
  );
  return data.data;
}

/** Show hidden feed lines again (Undo, or the hidden list). */
export async function unhideNeedsAttention(params: {
  companyId: string;
  branchId?: string;
  itemIds: string[];
}) {
  const { companyId, branchId, itemIds } = params;
  const { data } = await api.post<ApiResponse<NeedsAttentionHideResult>>(
    `/companies/${companyId}/needs-attention/unhide`,
    { itemIds },
    { params: branchId ? { branchId } : undefined }
  );
  return data;
}

export async function approveCompanyLeaveRequest(params: ApproveLeaveParams) {
  const { companyId, leaveRequestId } = params;
  const res = await api.put(
    `/companies/${companyId}/leave-requests-management/${leaveRequestId}/approve`
  );
  return res.data;
}

export async function rejectCompanyLeaveRequest(
  params: RejectCompanyLeaveParams
) {
  const { companyId, leaveRequestId, rejectionReason } = params;

  const res = await api.put(
    `/companies/${companyId}/leave-requests-management/${leaveRequestId}/reject`,
    { rejectionReason }
  );

  return res.data;
}

export async function deleteCompanyLeaveRequest(params: DeleteLeaveParams) {
  const { companyId, id } = params;
  const res = await api.delete(
    `/companies/${companyId}/leave-requests-management/${id}/delete`
  );
  return res.data;
}

export async function getCompanyLeaveCalendar(
  params: GetCompanyLeaveCalendarParams
) {
  const { companyId, ...queryParams } = params;
  const { data } = await api.get<CompanyLeaveCalendarResponse>(
    `/companies/${companyId}/company-leaves-calendar`,
    { params: queryParams }
  );
  return data;
}

export async function createCompanyLeaveRequest(
  companyId: string,
  payload: AdminCreateLeaveRequestManagementPayload
) {
  const { data } = await api.post(
    `/companies/${companyId}/leave-requests-management`,
    payload
  );

  return data;
}

export interface LeaveAttachmentUploadMeta {
  fileName: string;
  contentType: LeaveAttachmentContentType;
  sizeBytes: number;
}

/**
 * A presigned PUT for a supporting document of a request HR records for
 * this employee (the key lands under that employee's prefix).
 */
export async function requestLeaveAttachmentUploadUrl(
  companyId: string,
  employeeId: string,
  meta: LeaveAttachmentUploadMeta
): Promise<LeaveAttachmentUploadTicket> {
  const { data } = await api.post<ApiResponse<LeaveAttachmentUploadTicket>>(
    `/companies/${companyId}/leave-requests-management/employees/${employeeId}/attachments/upload-url`,
    meta
  );
  return data.data;
}

export async function getEmployeeWorkDayMinutes(
  companyId: string,
  employeeId: string
) {
  const { data } = await api.get<WorkDayMinutesResponse>(
    `/companies/${companyId}/leave-requests-management/employees/${employeeId}/work-day-minutes`
  );
  return data;
}

/**
 * Days a leave over these dates would deduct for this employee (their branch
 * holidays on the observed day), counted by the server as a saved request.
 */
export async function getEmployeeLeaveDayCount(
  companyId: string,
  employeeId: string,
  params: LeaveDayCountParams
) {
  const { data } = await api.get<ApiResponse<LeaveDayCount>>(
    `/companies/${companyId}/leave-requests-management/employees/${employeeId}/day-count`,
    { params }
  );
  return data;
}

export async function getCompanyLeaveWallets(
  params: GetCompanyLeaveWalletsParams
) {
  const { companyId, ...queryParams } = params;

  const { data } = await api.get<GetEmployeeLeaveWalletsResponse>(
    `/companies/${companyId}/company-leave-wallets`,
    {
      params: queryParams,
    }
  );

  return data;
}

export async function getLinkedWallets(params: {
  companyId: string;
  leaveRequestId: string;
}) {
  const { companyId, leaveRequestId } = params;

  const { data } = await api.get(
    `/companies/${companyId}/leave-requests-management/${leaveRequestId}/linked-wallet`
  );

  return data;
}

export async function getAllocationJobs(params: GetAllocationJobsParams) {
  const { companyId, policyId, ...queryParams } = params;

  const { data } = await api.get<LeaveJobResponse>(
    `/companies/${companyId}/leaves-management-schedule-and-jobs/policies/${policyId}/allocation-jobs`,
    {
      params: queryParams,
    }
  );

  return data.data;
}

export const getCurrentFiscalStartYear = async (
  companyId: string
): Promise<GetCurrentFiscalStartYearResponse> => {
  const { data } = await api.get<GetCurrentFiscalStartYearResponse>(
    `/companies/${companyId}/leave-settings/current-fiscal-start-year`
  );

  return data;
};

export async function getScheduledJobEmployeeData(
  params: GetScheduledJobEmployeeDataParams
) {
  const { companyId, scheduledJobId, ...queryParams } = params;

  const { data } = await api.get<ScheduledJobEmployeeResponse>(
    `/companies/${companyId}/leaves-management-schedule-and-jobs/scheduled-jobs/${scheduledJobId}/get-employee-data`,
    {
      params: queryParams,
    }
  );

  return data.data;
}
